import { useState } from 'react';

import type { RevenueBucket } from '../lib/reports';
import { formatMoneyCents } from '../lib/format';

type RevenueDaysChartProps = {
  buckets: RevenueBucket[];
};

/**
 * Ingresos over the period, one column per day (or week, or month). A single
 * series, so the section title names it and there is no legend; the exact
 * value of every column lives in the readout on hover or focus and in the
 * table screen readers get.
 */
export function RevenueDaysChart({ buckets }: RevenueDaysChartProps) {
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(...buckets.map((bucket) => bucket.revenueCents), 0);
  const shown = active === null ? null : buckets[active];
  if (buckets.length === 0 || max === 0) {
    return <p className="row-sub report-chart-empty">Sin ingresos en este período.</p>;
  }

  return (
    <div className="report-chart">
      <p className="report-chart-readout" aria-hidden="true">
        {shown ? (
          <>
            <span className="report-chart-readout-value">{formatMoneyCents(shown.revenueCents)}</span>
            <span className="row-sub">{shown.label}</span>
          </>
        ) : (
          <span className="row-sub">Toca una barra para ver el día.</span>
        )}
      </p>
      <div
        className="report-chart-plot"
        data-dense={buckets.length > 20 || undefined}
        onPointerLeave={() => setActive(null)}
        aria-hidden="true"
      >
        {buckets.map((bucket, index) => (
          <button
            key={bucket.key}
            type="button"
            tabIndex={-1}
            className="report-chart-column"
            data-active={active === index || undefined}
            onPointerEnter={() => setActive(index)}
            onFocus={() => setActive(index)}
            onClick={() => setActive(index)}
          >
            <span
              className="report-chart-bar"
              style={{ height: `${Math.max((bucket.revenueCents / max) * 100, bucket.revenueCents > 0 ? 2 : 0)}%` }}
            />
          </button>
        ))}
      </div>
      <div className="report-chart-axis" aria-hidden="true">
        <span>{buckets[0]!.label}</span>
        <span>{buckets[buckets.length - 1]!.label}</span>
      </div>
      <table className="sr-only">
        <caption>Ingresos por período</caption>
        <tbody>
          {buckets.map((bucket) => (
            <tr key={bucket.key}>
              <th scope="row">{bucket.label}</th>
              <td>{formatMoneyCents(bucket.revenueCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
